import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tabs,
  Tab,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import { CompanyInfo } from "../../Models/company";
import { useCertifications } from "../../Query/useCertifications";
import { FilteredContentCase } from "../../Enums/companyStatus";
import { useTranslation } from "react-i18next";
import { useDownloadFilteredContent } from "../../Query/useCompanies";
import { downloadDocumentBlob } from "../../Helpers/documentHelper";
import color from "../../Constants/colors";
import { formatDate } from "../../Helpers/dateHelpers";

interface FilteredContentProps {
  open: boolean;
  onClose: () => void;
  companyInfo: CompanyInfo;
}

const FilteredContent: React.FC<FilteredContentProps> = ({
  open,
  onClose,
  companyInfo,
}) => {
  const { data: categoriesData } = useCertifications();
  const [tabValue, setTabValue] = useState(FilteredContentCase.ALL);
  const { t } = useTranslation("singleCompanyComponent");
  const downloadFilteredContentMutation = useDownloadFilteredContent();
  const entityTypeMap = {
    [FilteredContentCase.ALL]: t("filteredContent.all"),
    [FilteredContentCase.ABOUT]: t("filteredContent.about"),
    [FilteredContentCase.DOCUMENT]: t("filteredContent.document"),
    [FilteredContentCase.WEBSITE]: t("filteredContent.website"),
  };

  const getCategoryNameById = (id: number): string => {
    const category = categoriesData?.find((cat) => cat.id === id);
    return category ? category.name : t("filteredContent.unknownCategory");
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const filteredRows = () => {
    switch (tabValue) {
      case FilteredContentCase.ALL:
        return [
          ...(companyInfo.filtered_data_about || []).map((content) => ({
            ...content,
            entityType: entityTypeMap[FilteredContentCase.ABOUT],
          })),
          ...(companyInfo.filtered_data_document || []).map((content) => ({
            ...content,
            entityType: entityTypeMap[FilteredContentCase.DOCUMENT],
          })),
          ...(companyInfo.filtered_data_website || []).map((content) => ({
            ...content,
            entityType: entityTypeMap[FilteredContentCase.WEBSITE],
          })),
        ];
      case FilteredContentCase.ABOUT:
        return (companyInfo.filtered_data_about || []).map((content) => ({
          ...content,
          entityType: entityTypeMap[FilteredContentCase.ABOUT],
        }));
      case FilteredContentCase.DOCUMENT:
        return (companyInfo.filtered_data_document || []).map((content) => ({
          ...content,
          entityType: entityTypeMap[FilteredContentCase.DOCUMENT],
        }));
      case FilteredContentCase.WEBSITE:
        return (companyInfo.filtered_data_website || []).map((content) => ({
          ...content,
          entityType: entityTypeMap[FilteredContentCase.WEBSITE],
        }));
      default:
        return [];
    }
  };

  const rows = filteredRows().map((content, index) => {
    let entityName = "";

    if (content.entityType === entityTypeMap[FilteredContentCase.ABOUT]) {
      entityName = `- ${t("filteredContent.aboutContent")}`;
    } else if (
      content.entityType === entityTypeMap[FilteredContentCase.DOCUMENT] &&
      content.entity_name
    ) {
      entityName = ` - ${content.entity_name}`;
    } else if (
      content.entityType === entityTypeMap[FilteredContentCase.WEBSITE] &&
      content.entity_name
    ) {
      entityName = ` - ${content.entity_name}`;
    } else {
      entityName = `- ${t("filteredContent.noSpecificContentAvailable")}`;
    }

    return {
      id: index + 1,
      certificationCategoryId: content.certification_category_id,
      filteredContentId: content.entity_id,
      lastEdit: content.creation_timestamp,
      entityId: content.entity_id,
      entityType: content.entityType,
      questionListName: content.question_list_name + entityName,
      questionListId: content.question_list_id,
    };
  });

  const handleDownload = async (
    entityId: string,
    entityType: string,
    questionListId: string | number
  ) => {
    downloadFilteredContentMutation.mutate(
      { entityType, entityId, questionListId },
      {
        onSuccess: (response) => {
          downloadDocumentBlob(
            response,
            `filtered_content_${entityType}_${entityId}.md`
          );
        },
      }
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      sx={{
        "& .MuiDialog-paper": {
          margin: 2,
          width: "85%",
          maxWidth: "1100px",
          borderRadius: "8px",
          border: `1px solid ${color.gray200}`,
          boxShadow:
            "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 24px",
          borderBottom: `1px solid ${color.gray200}`,
          color: color.primary_dark,
          fontSize: "1.25rem",
          fontWeight: 600,
        }}
      >
        {t("filteredContent.filteredContentTitle")}
        <IconButton
          onClick={onClose}
          sx={{
            padding: "8px",
            borderRadius: "4px",
            "&:hover": { backgroundColor: color.gray100 },
          }}
        >
          <CloseIcon sx={{ color: color.gray500 }} />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ padding: "20px 24px 12px 24px" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="inherit"
          sx={{
            marginBottom: 2,
            minHeight: 40,
            backgroundColor: color.gray50,
            borderRadius: "8px",
            padding: "4px",
            "& .MuiTabs-indicator": {
              display: "none",
            },
            "& .MuiTab-root": {
              color: color.primary_dark,
            },
            "& .MuiTab-root:hover": {
              color: color.primary_dark,
              backgroundColor: color.white,
            },
            "& .MuiTab-root.Mui-selected": {
              color: color.primary_dark,
              backgroundColor: "transparent",
            },
          }}
        >
          <Tab
            label={t("filteredContent.all")}
            value={FilteredContentCase.ALL}
            sx={{
              fontWeight: 600,
              color: color.primary_dark,
              minHeight: 36,
              borderRadius: "6px",
              "&:hover": {
                color: color.primary_dark,
                backgroundColor: color.white,
              },
              "&.Mui-selected": {
                color: color.primary_dark,
                backgroundColor: color.white,
              },
            }}
          />
          <Tab
            label={t("filteredContent.about")}
            value={FilteredContentCase.ABOUT}
            sx={{
              fontWeight: 600,
              color: color.primary_dark,
              minHeight: 36,
              borderRadius: "6px",
              "&:hover": {
                color: color.primary_dark,
                backgroundColor: color.white,
              },
              "&.Mui-selected": {
                color: color.primary_dark,
                backgroundColor: color.white,
              },
            }}
          />
          <Tab
            label={t("filteredContent.document")}
            value={FilteredContentCase.DOCUMENT}
            sx={{
              fontWeight: 600,
              color: color.primary_dark,
              minHeight: 36,
              borderRadius: "6px",
              "&:hover": {
                color: color.primary_dark,
                backgroundColor: color.white,
              },
              "&.Mui-selected": {
                color: color.primary_dark,
                backgroundColor: color.white,
              },
            }}
          />
          <Tab
            label={t("filteredContent.website")}
            value={FilteredContentCase.WEBSITE}
            sx={{
              fontWeight: 600,
              color: color.primary_dark,
              minHeight: 36,
              borderRadius: "6px",
              "&:hover": {
                color: color.primary_dark,
                backgroundColor: color.white,
              },
              "&.Mui-selected": {
                color: color.primary_dark,
                backgroundColor: color.white,
              },
            }}
          />
        </Tabs>
        <TableContainer
          sx={{
            marginTop: 1,
            border: `1px solid ${color.gray200}`,
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: color.white,
          }}
        >
          <Table size="small" sx={{ tableLayout: "fixed" }}>
            <TableHead>
              <TableRow>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: color.gray700,
                    backgroundColor: color.gray50,
                    borderBottom: `1px solid ${color.gray200}`,
                    fontSize: "0.8rem",
                    width: { xs: "160px", md: "220px" },
                  }}
                >
                  {t("filteredContent.evaluationCategory")}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: color.gray700,
                    backgroundColor: color.gray50,
                    borderBottom: `1px solid ${color.gray200}`,
                    fontSize: "0.8rem",
                    width: "auto",
                  }}
                >
                  {t("filteredContent.questionListAndContentDescription")}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: color.gray700,
                    backgroundColor: color.gray50,
                    borderBottom: `1px solid ${color.gray200}`,
                    fontSize: "0.8rem",
                    width: { xs: "120px", md: "160px" },
                  }}
                >
                  {t("filteredContent.lastEdit")}
                </TableCell>
                <TableCell
                  sx={{
                    fontWeight: 600,
                    color: color.gray700,
                    backgroundColor: color.gray50,
                    borderBottom: `1px solid ${color.gray200}`,
                    fontSize: "0.8rem",
                    width: "80px",
                  }}
                >
                  {t("filteredContent.action")}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length > 0 ? (
                rows.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell
                      sx={{ color: color.primary_dark, fontSize: "0.85rem" }}
                    >
                      {getCategoryNameById(row.certificationCategoryId)}
                    </TableCell>
                    <TableCell
                      sx={{ color: color.primary_dark, fontSize: "0.85rem" }}
                    >
                      <span
                        style={{
                          display: "block",
                          width: "100%",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                        title={row.questionListName}
                      >
                        {row.questionListName}
                      </span>
                    </TableCell>
                    <TableCell
                      sx={{ color: color.primary_dark, fontSize: "0.85rem" }}
                    >
                      {formatDate(row.lastEdit)}
                    </TableCell>
                    <TableCell sx={{ color: color.primary_dark }}>
                      <IconButton
                        onClick={() =>
                          handleDownload(
                            row.entityId.toString(),
                            row.entityType,
                            row.questionListId.toString()
                          )
                        }
                        sx={{
                          color: color.accent,
                          backgroundColor: "transparent",
                          "&:hover": { backgroundColor: "transparent" },
                        }}
                      >
                        <DownloadIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={4}
                    sx={{
                      color: color.gray600,
                      textAlign: "center",
                      padding: 4,
                      backgroundColor: color.gray50,
                    }}
                  >
                    {t("filteredContent.noFilteredContentAvailable")}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>
    </Dialog>
  );
};

export default FilteredContent;
